import { sameRequestKey } from "./pagination.utils";
import type { ID, Page, PageRequestKey } from "./types";

/**
 * Arguments accepted by {@link PagedList.addPage}. Object form so the call
 * site is self-documenting and the leaf-only `startIndex` can be omitted
 * without trailing-undefined boilerplate.
 */
export interface AddPageArgs<T = unknown> {
  items: T[];
  requestCursor: PageRequestKey;
  beforeCursor: string | null;
  afterCursor: string | null;
  direction: "after" | "before";
  startIndex?: number;
}

/**
 * Arguments accepted by {@link PagedList.replacePageItems}. Object form for
 * the same reasons as {@link AddPageArgs}.
 */
export interface ReplacePageItemsArgs<T = unknown> {
  requestCursor: PageRequestKey;
  newItems: T[];
  beforeCursor: string | null;
  afterCursor: string | null;
  startIndex?: number;
}

/**
 * Shift `firstItemOffsetInScope` by `-1` for every page at index `>= fromIdx`.
 * Null-offset pages are returned untouched — only numeric offsets shift.
 * Used by `PagedList.removeItem` to propagate a single-item deletion through
 * the offsets of the loaded window.
 */
function shiftOffsetsFrom<T>(pages: ReadonlyArray<Page<T>>, fromIdx: number): Array<Page<T>> {
  return pages.map((page, idx) => {
    if (idx < fromIdx || page.firstItemOffsetInScope === null) {
      return page;
    }
    return { ...page, firstItemOffsetInScope: page.firstItemOffsetInScope - 1 };
  });
}

/**
 * Per-list after-side overlay. Covers the two edges where per-page cursor
 * storage alone is not expressive enough:
 *
 *   1. "Empty response + after=null" — the backend tells us no more after
 *      data exists, but no page gets added (no items). Per-page storage
 *      can't record the exhaustion because there is no page to attach
 *      it to.
 *
 *   2. "All items deduped + advanced after cursor" — the backend returned
 *      pure duplicates but shifted its after cursor forward. Again no page
 *      is added; the new cursor lives on the overlay.
 *
 * When at least one page exists, the after-end page's `afterCursor` is the
 * source of truth for the derived `afterCursor` — `afterOverlay` is kept
 * in sync but the page wins if they disagree. When the list is empty
 * the overlay IS the source of truth.
 */
interface AfterOverlay {
  after: string | null;
  hasMore: boolean;
}

/**
 * Per-list before-side overlay. Mirror of `AfterOverlay` — keeps the
 * before cursor and `hasMore` flag expressible when the list has zero
 * pages (fresh empty, before-trimmed-to-empty, or after a `fromLast` /
 * `before` fetch returned no new items but advanced the cursor).
 *
 * Default for fresh empty: `hasMore=true` — we do not yet know whether
 * before data exists, but `step (2c)` can probe via `fromLast` (no
 * cursor needed), so we leave the door open. `markBeforeExhausted`
 * flips it to `false` when an explicit before probe confirms no data
 * on that side.
 */
interface BeforeOverlay {
  before: string | null;
  hasMore: boolean;
}

/** Sets of item IDs near each end of the window — output of `getDangerZone`. */
export interface DangerZone {
  start: Set<ID>;
  end: Set<ID>;
}

/** Boundary proximity flags — output of `isNearBoundary`. */
export interface BoundaryProximity {
  nearStart: boolean;
  nearEnd: boolean;
}

/** Neighboring item ids across page boundaries — output of `findItemNeighbors`. */
export interface ItemNeighbors {
  prevId: ID | null;
  nextId: ID | null;
}

/**
 * Result of `removeItem`: the new list plus the removed item's neighbors
 * (as they were *before* removal — used for cursor smoothing).
 */
export interface ItemRemovalResult<T> {
  list: PagedList<T>;
  prevId: ID | null;
  nextId: ID | null;
}

/**
 * Immutable sliding window of pages.
 * Every mutation returns a new instance — original is never modified.
 * Caches `items` (flat array) and `dangerZone` (start/end Sets) per instance.
 */
export class PagedList<T> {
  readonly pages: ReadonlyArray<Readonly<Page<T>>>;

  private readonly afterOverlay: AfterOverlay;
  private readonly beforeOverlay: BeforeOverlay;
  private readonly getKey: (item: T) => ID;
  private _items: T[] | null = null;
  private _dangerZone: DangerZone | null = null;
  private _dangerZoneThreshold: number | null = null;
  private readonly _staleKeys: PageRequestKey[];

  private constructor(
    pages: ReadonlyArray<Readonly<Page<T>>>,
    afterOverlay: AfterOverlay,
    beforeOverlay: BeforeOverlay,
    getKey: (item: T) => ID,
    staleKeys: PageRequestKey[],
  ) {
    this.pages = pages;
    this.afterOverlay = afterOverlay;
    this.beforeOverlay = beforeOverlay;
    this.getKey = getKey;
    this._staleKeys = staleKeys;
  }

  static empty<T>(getKey: (item: T) => ID): PagedList<T> {
    return new PagedList<T>(
      [],
      { after: null, hasMore: true },
      { before: null, hasMore: true },
      getKey,
      [],
    );
  }

  get items(): T[] {
    if (this._items === null) {
      const result: T[] = [];
      for (const page of this.pages) {
        for (const item of page.items) {
          result.push(item);
        }
      }
      this._items = result;
    }
    return this._items;
  }

  get length(): number {
    return this.pages.length;
  }

  get isEmpty(): boolean {
    return this.pages.length === 0;
  }

  /**
   * Cursor that identifies the page BEFORE our current before-end. `null`
   * ⇒ the before-end we know about is the very first page (no before
   * data) OR we have not yet learned a before cursor (fresh empty,
   * fromLast not run). When empty, falls back to overlay.
   */
  get beforeCursor(): string | null {
    if (this.pages.length) {
      return this.pages[0].beforeCursor;
    }
    return this.beforeOverlay.before;
  }

  /** Cursor that identifies the page AFTER our current after-end. When empty, falls back to overlay. */
  get afterCursor(): string | null {
    if (this.pages.length) {
      return this.pages[this.pages.length - 1].afterCursor;
    }
    return this.afterOverlay.after;
  }

  /**
   * Whether another page can be fetched in the given direction.
   *
   * When pages exist, the boundary page's response cursor is the source
   * of truth (a `null` cursor from the backend means "no more on this
   * side"). The overlay only matters for the empty-list case where
   * there is no page to read from. Symmetric on both sides.
   */
  canLoad(direction: "after" | "before"): boolean {
    if (direction === "after") {
      if (this.pages.length) {
        return this.pages[this.pages.length - 1].afterCursor !== null;
      }
      return this.afterOverlay.hasMore;
    }
    if (this.pages.length) {
      return this.pages[0].beforeCursor !== null;
    }
    return this.beforeOverlay.hasMore;
  }

  /**
   * `true` when the user has actually pulled records from above this
   * node — either via a `fromLast` probe (the scroll-up bootstrap) or
   * an explicit `before`-direction load. A forward-only history
   * (`kind: "after"` pages) returns `false` even if the server reports
   * a non-null `beforeCursor` on the first page: that cursor is just
   * an API hint, not user intent.
   *
   * The projection's backward chain (`isSubtreeComplete` on the
   * `"before"` side) uses this flag to decide whether
   * `canLoad("before") = true` should block earlier siblings. We only
   * block when the user has actually scrolled up — otherwise the chain
   * would hide siblings indefinitely whenever the server surfaces an
   * incidental `beforeCursor` on a forward bootstrap.
   */
  get wasScrolledUp(): boolean {
    for (const page of this.pages) {
      const kind = page.requestCursor.kind;
      if (kind === "fromLast" || kind === "before") {
        return true;
      }
    }
    return false;
  }

  getCursorFor(direction: "after" | "before"): string | null {
    return direction === "after" ? this.afterCursor : this.beforeCursor;
  }

  /**
   * Returns Sets of item IDs near the start and end of the window.
   * Cached on this immutable instance — compute once.
   */
  getDangerZone(threshold: number): DangerZone {
    if (this._dangerZone !== null && threshold === this._dangerZoneThreshold) {
      return this._dangerZone;
    }
    const zone: DangerZone = {
      start: this.collectFirstItemIds(threshold),
      end: this.collectLastItemIds(threshold),
    };
    this._dangerZone = zone;
    this._dangerZoneThreshold = threshold;
    return zone;
  }

  private collectFirstItemIds(count: number): Set<ID> {
    const ids = new Set<ID>();
    for (const page of this.pages) {
      for (const id of page.itemIds) {
        if (ids.size >= count) {
          return ids;
        }
        ids.add(id);
      }
    }
    return ids;
  }

  private collectLastItemIds(count: number): Set<ID> {
    const ids = new Set<ID>();
    for (let pageIdx = this.pages.length - 1; pageIdx >= 0; pageIdx--) {
      const page = this.pages[pageIdx];
      for (let i = page.itemIds.length - 1; i >= 0; i--) {
        if (ids.size >= count) {
          return ids;
        }
        ids.add(page.itemIds[i]);
      }
    }
    return ids;
  }

  /**
   * Checks whether any of the visible IDs fall within the danger zone.
   *
   * Iterates the (smaller) danger zone sets and checks membership in
   * visibleIds — O(threshold) instead of O(|visibleIds|).
   */
  isNearBoundary(visibleIds: ReadonlySet<ID>, threshold: number): BoundaryProximity {
    const zone = this.getDangerZone(threshold);
    let nearStart = false;
    let nearEnd = false;
    for (const id of zone.start) {
      if (visibleIds.has(id)) {
        nearStart = true;
        break;
      }
    }
    for (const id of zone.end) {
      if (visibleIds.has(id)) {
        nearEnd = true;
        break;
      }
    }
    return { nearStart, nearEnd };
  }

  /**
   * Checks whether any loaded page was fetched using the given request key.
   * Structural comparison — `{ kind: "fromLast" }` matches only itself;
   * `{ kind: "after", cursor: "x" }` does not match `{ kind: "before", cursor: "x" }`.
   */
  hasPageWithCursor(key: PageRequestKey): boolean {
    return this.pages.some((page) => sameRequestKey(page.requestCursor, key));
  }

  get hasStalePages(): boolean {
    return !!this._staleKeys.length;
  }

  isPageStale(key: PageRequestKey): boolean {
    return this._staleKeys.some((staleKey) => sameRequestKey(staleKey, key));
  }

  /** Mark all currently loaded pages as stale. Mutates in place — stale is orthogonal to immutability. */
  markAllStale(): void {
    this._staleKeys.length = 0;
    for (const page of this.pages) {
      this._staleKeys.push(page.requestCursor);
    }
  }

  clearPageStale(key: PageRequestKey): void {
    const idx = this._staleKeys.findIndex((staleKey) => sameRequestKey(staleKey, key));
    if (idx >= 0) {
      this._staleKeys.splice(idx, 1);
    }
  }

  clearAllStale(): void {
    this._staleKeys.length = 0;
  }

  /**
   * Add a page. Deduplicates items by key.
   *
   * Edge cases:
   *   - All items deduped + cursor advanced → return a new list with the
   *     advance recorded on the after-side or before-side overlay without adding
   *     a page. This keeps `getCursorFor(...)` monotonic so walkNode will
   *     not re-fetch the same cursor on the next cycle.
   *   - All items deduped + cursor unchanged → SAME instance returned.
   */
  addPage(args: AddPageArgs<T>): PagedList<T> {
    const { items, requestCursor, beforeCursor, afterCursor, direction, startIndex } = args;
    if (this.hasPageWithCursor(requestCursor)) {
      return this;
    }

    const existingIds = new Set<ID>();
    for (const page of this.pages) {
      for (const id of page.itemIds) {
        existingIds.add(id);
      }
    }
    const newItems = items.filter((item) => !existingIds.has(this.getKey(item)));

    if (newItems.length === 0) {
      if (direction === "after") {
        return this.advanceAfterCursor(afterCursor);
      }
      return this.advanceBeforeCursor(beforeCursor);
    }

    const newItemIds = newItems.map((item) => this.getKey(item));
    const page: Page<T> = {
      items: newItems,
      itemIds: newItemIds,
      requestCursor,
      beforeCursor,
      afterCursor,
      firstItemOffsetInScope: startIndex ?? null,
    };

    const staleKeys = [...this._staleKeys];

    if (direction === "after") {
      const newAfterOverlay: AfterOverlay = {
        after: afterCursor,
        hasMore: afterCursor !== null,
      };
      // Appending the very first page also seeds the before overlay so
      // a subsequent before-trim back to empty has a meaningful before
      // cursor to record.
      const newBeforeOverlay: BeforeOverlay =
        this.pages.length === 0
          ? { before: beforeCursor, hasMore: beforeCursor !== null }
          : this.beforeOverlay;
      return new PagedList<T>(
        [...this.pages, page],
        newAfterOverlay,
        newBeforeOverlay,
        this.getKey,
        staleKeys,
      );
    }

    // direction === "before": prepend. After overlay unchanged —
    // prepending a page does not affect the after-side exhaustion signal.
    // Before overlay mirrors the new before-end page so a future
    // before-trim-to-empty preserves the right before-side state.
    const newBeforeOverlay: BeforeOverlay = {
      before: beforeCursor,
      hasMore: beforeCursor !== null,
    };
    return new PagedList<T>(
      [page, ...this.pages],
      this.afterOverlay,
      newBeforeOverlay,
      this.getKey,
      staleKeys,
    );
  }

  /**
   * Record an after-side cursor advance without adding a page. Called on
   * the "all items deduped" edge. Returns SAME instance if nothing changed.
   */
  private advanceAfterCursor(newAfterCursor: string | null): PagedList<T> {
    if (newAfterCursor === this.afterCursor) {
      return this;
    }

    const newOverlay: AfterOverlay = {
      after: newAfterCursor,
      hasMore: newAfterCursor !== null,
    };

    if (this.pages.length === 0) {
      return new PagedList<T>(this.pages, newOverlay, this.beforeOverlay, this.getKey, [
        ...this._staleKeys,
      ]);
    }

    // Clone the after-end page so the derived `afterCursor` (which reads from
    // the after-end page) stays consistent with the overlay.
    const lastIdx = this.pages.length - 1;
    const lastPage = this.pages[lastIdx];
    const clonedAfter: Page<T> = {
      items: lastPage.items,
      itemIds: lastPage.itemIds,
      requestCursor: lastPage.requestCursor,
      beforeCursor: lastPage.beforeCursor,
      afterCursor: newAfterCursor,
      firstItemOffsetInScope: lastPage.firstItemOffsetInScope,
    };
    const newPages = this.pages.slice(0, lastIdx);
    newPages.push(clonedAfter);
    return new PagedList<T>(newPages, newOverlay, this.beforeOverlay, this.getKey, [
      ...this._staleKeys,
    ]);
  }

  /**
   * Record a before-side cursor advance without adding a page.
   * Symmetric to `advanceAfterCursor`. Updates the before overlay when
   * the list is empty (so a `fromLast` miss can record `hasMore=false`
   * and break fromLast loop attempts).
   */
  private advanceBeforeCursor(newBeforeCursor: string | null): PagedList<T> {
    if (newBeforeCursor === this.beforeCursor) {
      return this;
    }

    const newOverlay: BeforeOverlay = {
      before: newBeforeCursor,
      hasMore: newBeforeCursor !== null,
    };

    if (this.pages.length === 0) {
      return new PagedList<T>(this.pages, this.afterOverlay, newOverlay, this.getKey, [
        ...this._staleKeys,
      ]);
    }

    // Clone the before-end page so the derived `beforeCursor` (which reads
    // from the before-end page) stays consistent with the overlay.
    const beforePage = this.pages[0];
    const clonedBefore: Page<T> = {
      items: beforePage.items,
      itemIds: beforePage.itemIds,
      requestCursor: beforePage.requestCursor,
      beforeCursor: newBeforeCursor,
      afterCursor: beforePage.afterCursor,
      firstItemOffsetInScope: beforePage.firstItemOffsetInScope,
    };
    const newPages = [clonedBefore, ...this.pages.slice(1)];
    return new PagedList<T>(newPages, this.afterOverlay, newOverlay, this.getKey, [
      ...this._staleKeys,
    ]);
  }

  /**
   * Remove a page from the specified end.
   * Returns the SAME instance if no pages to remove.
   *
   * Per-page cursors carry the before/after identity — no
   * `restoredAfterCursor` parameter needed. Removing the after-end page
   * exposes the new after-end page's `afterCursor` as the forward cursor;
   * if the list becomes empty after the removal, both overlays reset
   * so a subsequent bootstrap or `fromLast` can recover the node.
   */
  removePage(from: "top" | "bottom"): PagedList<T> {
    if (this.pages.length === 0) {
      return this;
    }

    const removedPage = from === "top" ? this.pages[0] : this.pages[this.pages.length - 1];

    // Drop stale entry for the removed page, if any.
    const staleKeys = this._staleKeys.filter(
      (staleKey) => !sameRequestKey(staleKey, removedPage.requestCursor),
    );

    if (from === "top") {
      const remaining = this.pages.slice(1);
      if (remaining.length) {
        // Reading from the new before-end page keeps `beforeCursor` and
        // `hasMoreBefore` correct — before overlay is unused while pages
        // exist, so we leave it untouched.
        return new PagedList<T>(
          remaining,
          this.afterOverlay,
          this.beforeOverlay,
          this.getKey,
          staleKeys,
        );
      }
    } else {
      // from === "bottom"
      const remaining = this.pages.slice(0, -1);
      if (remaining.length) {
        const newAfterPage = remaining[remaining.length - 1];
        const newOverlay: AfterOverlay = {
          after: newAfterPage.afterCursor,
          hasMore: newAfterPage.afterCursor !== null,
        };
        return new PagedList<T>(remaining, newOverlay, this.beforeOverlay, this.getKey, staleKeys);
      }
    }

    // List becomes empty (top OR bottom trim of the last remaining page).
    // Reset to a fresh-empty overlay so a subsequent bootstrap (forward)
    // or `fromLast` (backward via step 2c) can recover — preserving the
    // trimmed page's exhausted cursors would lock the node forever.
    return PagedList.empty<T>(this.getKey);
  }

  /**
   * Record explicit exhaustion of the after side: returns a list with
   * `hasMore=false` and `after=null` on the after overlay. Called on the
   * "empty response + afterCursor=null + direction after" edge so we do
   * not loop trying to bootstrap a node the backend has confirmed has
   * no after-side data.
   *
   * If the current after-end page has a non-null `afterCursor`, the
   * after-end page is cloned with `afterCursor=null` so the derived
   * `afterCursor` stays consistent with the overlay.
   */
  markAfterExhausted(): PagedList<T> {
    if (!this.afterOverlay.hasMore && this.afterCursor === null) {
      return this;
    }

    const newOverlay: AfterOverlay = { after: null, hasMore: false };

    if (this.pages.length === 0) {
      return new PagedList<T>(this.pages, newOverlay, this.beforeOverlay, this.getKey, [
        ...this._staleKeys,
      ]);
    }

    const lastIdx = this.pages.length - 1;
    const lastPage = this.pages[lastIdx];
    if (lastPage.afterCursor === null) {
      return new PagedList<T>(this.pages, newOverlay, this.beforeOverlay, this.getKey, [
        ...this._staleKeys,
      ]);
    }

    const clonedAfter: Page<T> = {
      items: lastPage.items,
      itemIds: lastPage.itemIds,
      requestCursor: lastPage.requestCursor,
      beforeCursor: lastPage.beforeCursor,
      afterCursor: null,
      firstItemOffsetInScope: lastPage.firstItemOffsetInScope,
    };
    const newPages = this.pages.slice(0, lastIdx);
    newPages.push(clonedAfter);
    return new PagedList<T>(newPages, newOverlay, this.beforeOverlay, this.getKey, [
      ...this._staleKeys,
    ]);
  }

  /**
   * Record explicit exhaustion of the before side: returns a list with
   * `hasMore=false` and `before=null` on the before overlay. Called on
   * the "empty response + beforeCursor=null + direction before" edge so
   * step (2c) does not re-fire the same `fromLast` against a sibling
   * the backend has confirmed has no before-side data.
   *
   * If the current before-end page has a non-null `beforeCursor`, the
   * before-end page is cloned with `beforeCursor=null` so the derived
   * `beforeCursor` stays consistent with the overlay.
   */
  markBeforeExhausted(): PagedList<T> {
    if (!this.beforeOverlay.hasMore && this.beforeCursor === null) {
      return this;
    }

    const newOverlay: BeforeOverlay = { before: null, hasMore: false };

    if (this.pages.length === 0) {
      return new PagedList<T>(this.pages, this.afterOverlay, newOverlay, this.getKey, [
        ...this._staleKeys,
      ]);
    }

    const beforePage = this.pages[0];
    if (beforePage.beforeCursor === null) {
      return new PagedList<T>(this.pages, this.afterOverlay, newOverlay, this.getKey, [
        ...this._staleKeys,
      ]);
    }

    const clonedBefore: Page<T> = {
      items: beforePage.items,
      itemIds: beforePage.itemIds,
      requestCursor: beforePage.requestCursor,
      beforeCursor: null,
      afterCursor: beforePage.afterCursor,
      firstItemOffsetInScope: beforePage.firstItemOffsetInScope,
    };
    const newPages = [clonedBefore, ...this.pages.slice(1)];
    return new PagedList<T>(newPages, this.afterOverlay, newOverlay, this.getKey, [
      ...this._staleKeys,
    ]);
  }

  /**
   * Replace items of a specific page identified by its request key.
   * Returns new PagedList with updated page, or SAME instance if the key
   * is not found.
   *
   * Both cursors are updated — a reload should reflect the backend's
   * latest neighbours. Clears the stale flag for the replaced page.
   * Throws if two pages share the same `requestCursor` (defensive guard
   * against a bookkeeping bug).
   */
  replacePageItems(args: ReplacePageItemsArgs<T>): PagedList<T> {
    const { requestCursor, newItems, beforeCursor, afterCursor, startIndex } = args;
    const idx = this.findUniquePageIndex(requestCursor);
    if (idx === -1) {
      return this;
    }

    const newPage = this.buildPage(newItems, requestCursor, beforeCursor, afterCursor, startIndex);
    const newPages = [...this.pages];
    newPages[idx] = newPage;

    const newAfterOverlay = this.replaceAfterOverlay(idx, afterCursor);
    const newBeforeOverlay = this.replaceBeforeOverlay(idx, beforeCursor);

    const staleKeys = this._staleKeys.filter(
      (staleKey) => !sameRequestKey(staleKey, requestCursor),
    );

    return new PagedList<T>(newPages, newAfterOverlay, newBeforeOverlay, this.getKey, staleKeys);
  }

  private findUniquePageIndex(requestCursor: PageRequestKey): number {
    const idx = this.pages.findIndex((page) => sameRequestKey(page.requestCursor, requestCursor));
    if (idx === -1) {
      return -1;
    }
    const duplicateIdx = this.pages.findIndex(
      (page, pageIdx) => pageIdx !== idx && sameRequestKey(page.requestCursor, requestCursor),
    );
    if (duplicateIdx !== -1) {
      throw new Error(
        `PagedList.replacePageItems: duplicate requestCursor at indices ${idx} and ${duplicateIdx}`,
      );
    }
    return idx;
  }

  private buildPage(
    items: T[],
    requestCursor: PageRequestKey,
    beforeCursor: string | null,
    afterCursor: string | null,
    startIndex: number | undefined,
  ): Page<T> {
    const itemIds = items.map((item) => this.getKey(item));
    return {
      items,
      itemIds,
      requestCursor,
      beforeCursor,
      afterCursor,
      firstItemOffsetInScope: startIndex ?? null,
    };
  }

  private replaceAfterOverlay(idx: number, afterCursor: string | null): AfterOverlay {
    if (idx !== this.pages.length - 1) {
      return this.afterOverlay;
    }
    return { after: afterCursor, hasMore: afterCursor !== null };
  }

  private replaceBeforeOverlay(idx: number, beforeCursor: string | null): BeforeOverlay {
    if (idx !== 0) {
      return this.beforeOverlay;
    }
    return { before: beforeCursor, hasMore: beforeCursor !== null };
  }

  /** Returns a fresh empty PagedList with the same getKey. */
  reset(): PagedList<T> {
    return PagedList.empty(this.getKey);
  }

  /**
   * Find the item by id and return its immediate neighbors across page
   * boundaries. Returns `{ prevId: null, nextId: null }` if the item is
   * not loaded.
   */
  findItemNeighbors(itemId: ID): ItemNeighbors {
    for (let pageIdx = 0; pageIdx < this.pages.length; pageIdx++) {
      const page = this.pages[pageIdx];
      const posInPage = page.itemIds.indexOf(itemId);
      if (posInPage === -1) {
        continue;
      }

      let prevId: ID | null = null;
      if (posInPage) {
        prevId = page.itemIds[posInPage - 1];
      } else if (pageIdx) {
        const prevPage = this.pages[pageIdx - 1];
        prevId = prevPage.itemIds[prevPage.itemIds.length - 1] ?? null;
      }

      let nextId: ID | null = null;
      if (posInPage < page.itemIds.length - 1) {
        nextId = page.itemIds[posInPage + 1];
      } else if (pageIdx < this.pages.length - 1) {
        nextId = this.pages[pageIdx + 1].itemIds[0] ?? null;
      }

      return { prevId, nextId };
    }
    return { prevId: null, nextId: null };
  }

  /**
   * Immutably replace one item by id. Returns the same instance if the
   * item is not found.
   */
  replaceItem(itemId: ID, newItem: T): PagedList<T> {
    for (let pageIdx = 0; pageIdx < this.pages.length; pageIdx++) {
      const page = this.pages[pageIdx];
      const posInPage = page.itemIds.indexOf(itemId);
      if (posInPage === -1) {
        continue;
      }

      const newItems = [...page.items] as T[];
      newItems[posInPage] = newItem;
      const newPage: Page<T> = {
        items: newItems,
        itemIds: page.itemIds,
        requestCursor: page.requestCursor,
        beforeCursor: page.beforeCursor,
        afterCursor: page.afterCursor,
        firstItemOffsetInScope: page.firstItemOffsetInScope,
      };
      const newPages = [...this.pages];
      newPages[pageIdx] = newPage;
      return new PagedList<T>(newPages, this.afterOverlay, this.beforeOverlay, this.getKey, [
        ...this._staleKeys,
      ]);
    }
    return this;
  }

  /**
   * Immutably remove one item by id. Returns the neighbors as they were
   * **before** removal (for cursor smoothing). Returns the same instance
   * if the item is not found.
   *
   * Pages strictly AFTER the affected page have their `firstItemOffsetInScope`
   * shifted by `-1` (null-offset pages are left unchanged). The affected
   * page keeps its own offset — the page-relative positions of its
   * surviving items naturally yield correct absolute indices.
   */
  removeItem(itemId: ID): ItemRemovalResult<T> {
    const neighbors = this.findItemNeighbors(itemId);

    for (let pageIdx = 0; pageIdx < this.pages.length; pageIdx++) {
      const page = this.pages[pageIdx];
      const posInPage = page.itemIds.indexOf(itemId);
      if (posInPage === -1) {
        continue;
      }

      const newItems = (page.items as T[]).filter((_, i) => i !== posInPage);
      const newItemIds = (page.itemIds as ID[]).filter((_, i) => i !== posInPage);

      const staleKeys = [...this._staleKeys];

      if (newItems.length === 0) {
        // Page becomes empty — splice it out entirely
        const splicedWithoutShift = [
          ...this.pages.slice(0, pageIdx),
          ...this.pages.slice(pageIdx + 1),
        ];
        if (splicedWithoutShift.length === 0) {
          return { list: PagedList.empty<T>(this.getKey), ...neighbors };
        }
        // After splicing, pages that were AFTER the removed page now sit at
        // indices >= pageIdx and need a `-1` offset shift.
        const splicedPages = shiftOffsetsFrom(splicedWithoutShift, pageIdx);
        const isLast = pageIdx === this.pages.length - 1;
        const isFirst = pageIdx === 0;
        const newAfterOverlay = isLast
          ? {
              after: splicedPages[splicedPages.length - 1].afterCursor,
              hasMore: splicedPages[splicedPages.length - 1].afterCursor !== null,
            }
          : this.afterOverlay;
        const newBeforeOverlay = isFirst
          ? { before: splicedPages[0].beforeCursor, hasMore: splicedPages[0].beforeCursor !== null }
          : this.beforeOverlay;
        const filteredStaleKeys = staleKeys.filter((sk) => !sameRequestKey(sk, page.requestCursor));
        return {
          list: new PagedList<T>(
            splicedPages,
            newAfterOverlay,
            newBeforeOverlay,
            this.getKey,
            filteredStaleKeys,
          ),
          ...neighbors,
        };
      }

      const newPage: Page<T> = {
        items: newItems,
        itemIds: newItemIds,
        requestCursor: page.requestCursor,
        beforeCursor: page.beforeCursor,
        afterCursor: page.afterCursor,
        firstItemOffsetInScope: page.firstItemOffsetInScope,
      };
      const newPagesUnshifted = [...this.pages];
      newPagesUnshifted[pageIdx] = newPage;
      // Affected page keeps its own offset; pages strictly after it shift by -1.
      const newPages = shiftOffsetsFrom(newPagesUnshifted, pageIdx + 1);

      return {
        list: new PagedList<T>(
          newPages,
          this.afterOverlay,
          this.beforeOverlay,
          this.getKey,
          staleKeys,
        ),
        ...neighbors,
      };
    }

    return { list: this, ...neighbors };
  }

  /**
   * Rewrite `requestCursor` entries that reference `deletedId` to point
   * to its former neighbor. Used for cursor smoothing after a record
   * removal so pending loads do not 404 on a stale cursor.
   *
   * Rules:
   *   - `{ kind: 'after', cursor: deletedId }` → `{ kind: 'after', cursor: prevId }`.
   *     If prevId is null → `{ kind: 'after', cursor: null }` (bootstrap).
   *   - `{ kind: 'before', cursor: deletedId }` → `{ kind: 'before', cursor: nextId }`.
   *     If nextId is null → `{ kind: 'fromLast' }`.
   *
   * Response cursors (`beforeCursor`/`afterCursor`) are NOT touched —
   * they are server-opaque values.
   */
  rewriteRequestCursor(deletedId: ID, prevId: ID | null, nextId: ID | null): PagedList<T> {
    let changed = false;
    const newPages = this.pages.map((page) => {
      const rc = page.requestCursor;
      let newCursor: PageRequestKey | null = null;

      if (rc.kind === "after" && rc.cursor === deletedId) {
        newCursor = { kind: "after", cursor: prevId };
      } else if (rc.kind === "before" && rc.cursor === deletedId) {
        newCursor = nextId !== null ? { kind: "before", cursor: nextId } : { kind: "fromLast" };
      }

      if (newCursor === null) {
        return page;
      }
      changed = true;
      return { ...page, requestCursor: newCursor } as Page<T>;
    });

    if (!changed) {
      return this;
    }
    return new PagedList<T>(newPages, this.afterOverlay, this.beforeOverlay, this.getKey, [
      ...this._staleKeys,
    ]);
  }
}
